/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import './PullRequestTimeline.css';

import type {
  Actor,
  ClosedEventItem,
  GitHubPullRequestReviewThread,
  HeadRefForcePushedEventItem,
  IssueCommentItem,
  MergedEventItem,
  PullRequestReviewItem,
  PullRequestTimelineItem,
  RenamedTitleEventItem,
  ReviewRequestedEventItem,
  ReviewRequestRemovedEventItem,
} from './github/pullRequestTimelineTypes';

import ActorHeading from './ActorHeading';
import CenteredSpinner from './CenteredSpinner';
import CommitLink from './CommitLink';
import PendingLabel from './PendingLabel';
import PullRequestReviewComment from './PullRequestReviewComment';
import ResolveThreadButton from './ResolveThreadButton';
import TrustedRenderedMarkdown from './TrustedRenderedMarkdown';
import timeAgo from './timeAgo';
import {
  gitHubOrgAndRepoAtom,
  gitHubPullRequestAtom,
  gitHubPullRequestHoveredThreadRangeAtom,
  gitHubPullRequestReviewThreadsByReviewIDAtom,
} from './jotai';
import {
  CheckCircleFillIcon,
  CheckCircleIcon,
  FileDiffIcon,
  GitMergeIcon,
  GitPullRequestClosedIcon,
} from '@primer/octicons-react';
import {Box, StyledOcticon, Text} from '@primer/react';
import {useAtomValue, useSetAtom} from 'jotai';
import React, {Suspense, useCallback, useMemo, useState} from 'react';
import {notEmpty} from 'shared/utils';

export default function PullRequestTimeline(): React.ReactElement {
  return (
    <Suspense fallback={<CenteredSpinner />}>
      <PullRequestTimelineBootstrap />
    </Suspense>
  );
}

function PullRequestTimelineBootstrap(): React.ReactElement {
  const pullRequest = useAtomValue(gitHubPullRequestAtom);
  const items = (pullRequest?.timelineItems.nodes ?? []).filter(notEmpty);
  let version = 1;
  return (
    <Box display="flex" flexDirection="column" gridGap={2}>
      <VersionBreak version={1} />
      {items.map((item, index) => {
        if (item.__typename === 'HeadRefForcePushedEvent') {
          ++version;
        }
        return <TimelineItem key={index} item={item} version={version} />;
      })}

      {/* Adds some padding after the last TimelineItem. */}
      <TimelineBasicEvent />
    </Box>
  );
}

function TimelineItem({
  item,
  version,
}: {
  item: PullRequestTimelineItem;
  version: number;
}): React.ReactElement | null {
  switch (item.__typename) {
    case 'PullRequestCommit':
      return null;
    case 'PullRequestReview':
      return <PullRequestReview item={item} />;
    case 'IssueComment':
      return <IssueComment item={item} />;
    case 'HeadRefForcePushedEvent':
      return <HeadRefForcePushedEvent item={item} version={version} />;
    case 'RenamedTitleEvent':
      return <RenamedTitleEvent item={item} />;
    case 'ReviewRequestedEvent':
      return <ReviewRequestEvent item={item} isRequested={true} />;
    case 'ReviewRequestRemovedEvent':
      return <ReviewRequestEvent item={item} isRequested={false} />;
    case 'MergedEvent':
      return <MergedEvent item={item} />;
    case 'ClosedEvent':
      return <ClosedEvent item={item} />;
    default:
      return null;
  }
}

function TimelineCallout(props: {
  actor?: Actor | null;
  children: React.ReactNode;
  isPending?: boolean;
  createdAt?: string | null;
}): React.ReactElement {
  return (
    <Box padding="4px 6px 0">
      <Box
        backgroundColor="canvas.default"
        color="fg.default"
        border={1}
        borderColor="border.default"
        borderStyle="solid"
        borderRadius="2px"
        padding="6px 8px">
        <Box display="flex" justifyContent="space-between">
          <ActorHeading
            actor={props.actor}
            timestamp={props.createdAt != null ? timeAgo(props.createdAt) : undefined}
          />
          {props.isPending && <PendingLabel />}
        </Box>
        {props.children}
      </Box>
    </Box>
  );
}

function TimelineBasicEvent(props: {
  children?: React.ReactNode;
  createdAt?: string | null;
}): React.ReactElement {
  return (
    <Box
      color="neutral.emphasis"
      fontSize="12px"
      lineHeight="14px"
      padding="4px 6px 0"
      display="flex"
      justifyContent="space-between"
      alignItems="center">
      <Box>{props.children}</Box>
      {props.createdAt != null && (
        <Text fontSize={11} color="fg.muted" sx={{flexShrink: 0, marginLeft: 2}}>
          {timeAgo(props.createdAt)}
        </Text>
      )}
    </Box>
  );
}

function PullRequestReview({item}: {item: PullRequestReviewItem}): React.ReactElement {
  const threadsByReview = useAtomValue(gitHubPullRequestReviewThreadsByReviewIDAtom);
  const threads = threadsByReview[item.id] ?? [];
  const comments = threads.map((thread, index) => (
    <SidebarThread key={`thread-${index}`} thread={thread} />
  ));
  const reviewTimestamp = item.submittedAt ?? null;
  let action = null;
  let isPending = false;
  switch (item.state) {
    // TODO(mbolin): Handle other cases? Need to find examples on GitHub.
    case 'APPROVED': {
      action = (
        <ReviewAction
          actor={item.author}
          action="accepted this revision"
          color="success.fg"
          icon={CheckCircleFillIcon}
          createdAt={reviewTimestamp}
        />
      );
      break;
    }
    case 'CHANGES_REQUESTED': {
      action = (
        <ReviewAction
          actor={item.author}
          action="requested changes to this revision"
          color="danger.fg"
          icon={FileDiffIcon}
          createdAt={reviewTimestamp}
        />
      );
      break;
    }
    case 'PENDING': {
      isPending = true;
      break;
    }
  }
  const hasContent = item.bodyHTML !== '' || comments.length > 0;

  return (
    <>
      {action}
      {hasContent && (
        <TimelineCallout actor={item.author} isPending={isPending} createdAt={item.submittedAt}>
          {item.bodyHTML !== '' && (
            <Box paddingY={1}>
              <TrustedRenderedMarkdown
                className="PRT-bodyHTML PRT-review-comment-text"
                trustedHTML={item.bodyHTML}
              />
            </Box>
          )}
          {comments.length > 0 && (
            <>
              <Box display="flex" alignItems="center" paddingY={1}>
                <Text fontSize="0.5rem" fontWeight="bold">
                  INLINE COMMENTS
                </Text>
                <HorizontalLine isPrefix={false} />
              </Box>

              {comments}
            </>
          )}
        </TimelineCallout>
      )}
    </>
  );
}

function ReviewAction({
  actor,
  action,
  color,
  icon,
  createdAt,
}: {
  actor?: Actor | null;
  action: string;
  color: string;
  icon: React.ElementType;
  createdAt?: string | null;
}) {
  return (
    <TimelineBasicEvent createdAt={createdAt}>
      <Box display="flex" gridGap={1} alignItems="center">
        <StyledOcticon icon={icon} color={color} /> <ActorBasic actor={actor} /> {action}
      </Box>
    </TimelineBasicEvent>
  );
}

function IssueComment({item}: {item: IssueCommentItem}): React.ReactElement {
  return (
    <TimelineCallout actor={item.author} createdAt={item.createdAt}>
      <TrustedRenderedMarkdown
        className="PRT-bodyHTML PRT-review-comment-text"
        trustedHTML={item.bodyHTML}
      />
    </TimelineCallout>
  );
}

function HeadRefForcePushedEvent({
  item,
  version,
}: {
  item: HeadRefForcePushedEventItem;
  version: number;
}): React.ReactElement {
  return (
    <>
      <VersionBreak version={version} />
      <TimelineBasicEvent createdAt={item.createdAt}>
        <ActorBasic actor={item.actor} /> updated this revision to Diff {version} (
        <code>{item.afterCommit?.oid.slice(0, 8)}</code>)
      </TimelineBasicEvent>
    </>
  );
}

function HorizontalLine({isPrefix}: {isPrefix: boolean}): React.ReactElement {
  return (
    <Box
      borderBottomStyle="solid"
      borderBottomWidth={1}
      borderColor="border.subtle"
      display="block"
      fontSize="0.625rem"
      fontWeight="bold"
      lineHeight={1.2}
      flexBasis={isPrefix ? '24px' : 'autho'}
      flexGrow={isPrefix ? 0 : 1}
      flexShrink={isPrefix ? 0 : 1}
      marginLeft={isPrefix ? 0 : '8px'}
      marginRight={isPrefix ? '8px' : 0}>
      {' '}
    </Box>
  );
}

function VersionBreak({version}: {version: number}): React.ReactElement {
  return (
    <Box paddingTop={1} paddingX="6px">
      <Box
        display="flex"
        alignItems="center"
        fontSize="10px"
        fontWeight={700}
        lineHeight="12px"
        paddingX={0}
        paddingY={1}>
        <HorizontalLine isPrefix={true} />
        <Text color="neutral.emphasis">Diff {version}</Text>
        <HorizontalLine isPrefix={false} />
      </Box>
    </Box>
  );
}

function ReviewRequestEvent({
  item,
  isRequested,
}: {
  item: ReviewRequestedEventItem | ReviewRequestRemovedEventItem;
  isRequested: boolean;
}): React.ReactElement {
  const requestedReviewer =
    item.requestedReviewer?.__typename === 'User' ||
    item.requestedReviewer?.__typename === 'Mannequin'
      ? item.requestedReviewer
      : null;
  const action = isRequested ? 'requested a review from' : 'removed review request for';

  return (
    <TimelineBasicEvent createdAt={item.createdAt}>
      <ActorBasic actor={item.actor} /> {action} <ActorBasic actor={requestedReviewer} />
    </TimelineBasicEvent>
  );
}

function RenamedTitleEvent({item}: {item: RenamedTitleEventItem}): React.ReactElement {
  return (
    <TimelineBasicEvent createdAt={item.createdAt}>
      <ActorBasic actor={item.actor} /> changed the title{' '}
      <span className="PRT-old-title">{item.previousTitle}</span> to{' '}
      <span className="PRT-new-title">{item.currentTitle}</span>
    </TimelineBasicEvent>
  );
}

function MergedEvent({item}: {item: MergedEventItem}) {
  const {org, repo} = useAtomValue(gitHubOrgAndRepoAtom) ?? {};
  const {actor, mergedCommit, mergeRefName} = item;
  const commitOID = mergedCommit?.oid;
  const commit =
    commitOID == null ? null : org == null || repo == null ? (
      commitOID
    ) : (
      <CommitLink org={org} repo={repo} oid={commitOID} />
    );

  return (
    <TimelineBasicEvent createdAt={item.createdAt}>
      <Box display="flex" gridGap={1} alignItems="center">
        <>
          <StyledOcticon icon={GitMergeIcon} color="done.fg" /> <ActorBasic actor={actor} /> merged
          commit {commit} into {mergeRefName}
        </>
      </Box>
    </TimelineBasicEvent>
  );
}

function ClosedEvent({item}: {item: ClosedEventItem}) {
  if (item.closable.__typename === 'PullRequest' && item.closable.merged) {
    return null;
  }

  return (
    <TimelineBasicEvent createdAt={item.createdAt}>
      <Box display="flex" gridGap={1} alignItems="center">
        <>
          <StyledOcticon icon={GitPullRequestClosedIcon} color="danger.fg" />{' '}
          <ActorBasic actor={item.actor} /> closed this pull request
        </>
      </Box>
    </TimelineBasicEvent>
  );
}

/**
 * Note that `ReviewRequestedEventItem.requestedReviewer` is not an `Actor`,
 * hence the duck type for the `actor` prop.
 */
function ActorBasic({actor}: {actor?: {login: string} | null}): React.ReactElement {
  const login = actor?.login ?? '[unknown]';
  return <span className="PRT-actor-basic">{login}</span>;
}

function SidebarThread({thread}: {thread: GitHubPullRequestReviewThread}): React.ReactElement {
  const [collapsed, setCollapsed] = useState(thread.isResolved);
  const firstComment = thread.comments[0];
  const setHoveredRange = useSetAtom(gitHubPullRequestHoveredThreadRangeAtom);

  const threadRange = useMemo(() => {
    const endLine = thread.originalLine;
    if (endLine == null || firstComment == null) return null;
    const startLine = thread.originalStartLine ?? endLine;
    return {
      path: firstComment.path,
      startLine,
      endLine,
      side: thread.startDiffSide ?? thread.diffSide,
    };
  }, [thread, firstComment]);

  const onMouseEnter = useCallback(() => {
    if (threadRange != null) setHoveredRange(threadRange);
  }, [threadRange, setHoveredRange]);

  const onMouseLeave = useCallback(() => {
    setHoveredRange(null);
  }, [setHoveredRange]);

  if (collapsed) {
    return (
      <Box
        display="flex"
        alignItems="center"
        borderColor="border.default"
        borderWidth={1}
        borderStyle="solid"
        borderRadius={2}
        marginBottom={2}
        paddingX={2}
        paddingY={1}
        sx={{gap: 1, cursor: 'pointer'}}
        onClick={() => setCollapsed(false)}>
        <StyledOcticon icon={CheckCircleIcon} size={14} color="done.fg" />
        <Text fontSize={12} color="fg.muted" sx={{flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
          {firstComment?.path}
        </Text>
        <Text fontSize={11} color="fg.muted">Resolved</Text>
      </Box>
    );
  }

  return (
    <Box
      borderColor="border.default"
      borderWidth={1}
      borderStyle="solid"
      borderRadius={2}
      overflow="hidden"
      marginBottom={2}
      padding={2}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}>
      {thread.comments.map((threadComment, threadIndex) => (
        <Box
          key={threadIndex}
          {...(threadIndex > 0 ? {
            borderTopWidth: 1,
            borderTopStyle: 'solid',
            borderTopColor: 'border.muted',
            marginTop: 2,
            paddingTop: 1,
          } : {})}>
          <PullRequestReviewComment
            comment={threadComment}
            isReply={threadIndex > 0}
            showPath={threadIndex === 0}
          />
        </Box>
      ))}
      <Box
        display="flex"
        alignItems="center"
        borderTopWidth={1}
        borderTopStyle="solid"
        borderTopColor="border.muted"
        marginTop={2}
        paddingTop={2}
        sx={{gap: 2}}>
        <ResolveThreadButton threadId={thread.id} isResolved={thread.isResolved} />
        {thread.isResolved && (
          <Text
            as="button"
            fontSize={12}
            onClick={() => setCollapsed(true)}
            sx={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: 'fg.muted',
              '&:hover': {textDecoration: 'underline'},
            }}>
            Collapse
          </Text>
        )}
      </Box>
    </Box>
  );
}
